/// <amd-module name="Analytics/Common/AnalyticsCacheableQueryBase" />
import { ODataQueryOptions } from "Analytics/Common/ODataQueryOptions";
import * as Resources from "Analytics/Common/Resources";
import { IAnalyticsODataClient } from "Analytics/Common/RestClient/AnalyticsODataClient";
import { ICacheableDataQualityQuery, ODataPayloadAndWarnings } from "Reporting/Common/ICacheableDataQualityQuery";
import { ITrackCacheHits } from "Reporting/Common/ICacheableQuery";
import { format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";

/** Common queries can use this name for identification when a query is not experience-specific. e.g. to populate a picker used across widgets.  */
export const CommonFeatureCommandName = "CommonQuery";

/* Implementation to handle query execution of cached queries operating on Ax, with standard transformation flow for strongly usage by consumers.
 *
 * Derivations are responsible for providing a strongly typed API contract around OData.
 *
 * In order to implement this contract, all query implementations must:
 *  1- implement getQueryName()
 *  2- Pass featureCommandName(for tracking) and OData queryOptions via super() on the constructor.
 */
export abstract class AnalyticsCacheableQueryBase<T> implements ICacheableDataQualityQuery<T>, ITrackCacheHits {
    private static readonly untrackedWebAutomationToken: string = "-untracked";
    /**
     * Describes "automatic" requests being made without any explicit user intent. Such requests are not counted on service as user activity.
     */
    private isAutomationRequest: boolean = false;
    constructor(private readonly _featureCommandName: string, private readonly _queryOptions: ODataQueryOptions) {}

    /**
     *  Runs the AX OData query and providing results in strongly typed form.
     *  As all AX queries go through a common codepath, derived types are not responsible for this.
     *
     * Note: The Cacheable Query service ignores this method where runDataQualityAwareQuery() is implemented.
     */
    public async runQuery(pageContext: IVssPageContext): Promise<T> {
        const data = await this.runqueryCore(pageContext);
        this.inspectResponseFormat(data);
        return this.interpretQueryResults(data);
    }

    /**
     *  Runs the AX OData query, providing both the results and data quality warnings in strongly typed form.
     *  As all AX queries go through a common codepath, derived types are not responsible for this.
     */
    public async runDataQualityAwareQuery(pageContext: IVssPageContext): Promise<ODataPayloadAndWarnings<T>> {
        const data = await this.runqueryCore(pageContext);
        const warning: ODataPayloadAndWarnings<T> = {
            data: this.interpretQueryResults(data),
            dataQualityWarnings: this.extractDataQualityWarnings(data)
        };
        return warning;
    }

    /*** Gets a unique key for describing the configuration of the query.
     * As all OData query options share a common configuration format, implementers don't need to handle this.
     */
    public getKey(): string {
        const clonedQueryoptions = { ...this._queryOptions };

        if (clonedQueryoptions.pageContext) {
            delete clonedQueryoptions.pageContext;
        }
        return this.getQueryName() + JSON.stringify(clonedQueryoptions);
    }

    /** Fluent Operator - Specifies if this query request should be ignored by Ax service, as it is part of an automation scenario. This concern is orthogonal to data shape or query identification. */
    public setAutomationMode(isAutomationRequest?: boolean): AnalyticsCacheableQueryBase<T> {
        this.isAutomationRequest = isAutomationRequest === true;
        return this;
    }

    /**
     * Called by Query Cache - Tracks cache hits bypassing the Ax Service.
     */
    public onCacheHit(pageContext: IVssPageContext): void {
        if (!this.isAutomationRequest) {
            publishCachedODataEvent(pageContext, this._queryOptions.entityType);
        }
    }

    /** Perform basic runtime format checking to verify results are well formed, before interpretQueryResults and downstream consumers operate on results.
     *
     * This exposes details of query config to support error diagnosis.
     */
    protected inspectResponseFormat(data: { value: any }) {
        // Unless overwritten, result should be an array or an object we always expect to be present(e.g. last value from entity like DateSK on Ping Service Query).
        if (data == null || data.value == null) {
            throw new Error(format(Resources.ODataPayloadEmptyError, this.getKey()));
        }
    }

    /**
     * Common OData payload extraction step.
     * Extensibility point for derived classes to reinterpret the results. Most queries should leave this as-is.
     */
    protected interpretQueryResults(data: { value: any }): T {
        return data.value;
    }

    protected extractDataQualityWarnings(data: { "@vsts.warnings": string[] }): string[] {
        const dataQualityWarnings: string[] = [];
        if (data["@vsts.warnings"]) {
            data["@vsts.warnings"].forEach(element => {
                if (element.startsWith("VS1670030")) {
                    dataQualityWarnings.push(element);
                }
            });
        }
        return dataQualityWarnings;
    }

    /** Used to determine appropriate key name of underlying query. This must be implemented by derived types. */
    protected abstract getQueryName(): string;

    private async runqueryCore(pageContext: IVssPageContext): Promise<any> {
        const queryOptions = this._queryOptions;
        queryOptions.pageContext = pageContext;
        const client = pageContext.getRestClient<IAnalyticsODataClient>("IAnalyticsODataClient");
        const data = await client.query(this.getFeatureCommandName(), queryOptions);
        this.inspectResponseFormat(data);
        return data;
    }

    private getFeatureCommandName() {
        const name = this.isAutomationRequest
            ? this._featureCommandName + AnalyticsCacheableQueryBase.untrackedWebAutomationToken
            : this._featureCommandName;
        return name;
    }
}

function publishCachedODataEvent(pageContext: IVssPageContext, entityType: string): void {
    const telemetryService = pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
    const areaName = "Reporting";
    const featureName = "Engagement";
    const properties = {
        Scenario: "InProductOData-Cached", // Allows for tracking of InProductOData events which were Cached
        EntityType: entityType
    };
    telemetryService.publishEvent(areaName, featureName, properties);
}
